import 'react-table/react-table.css';
import '../css/WBReactTable.less';
import * as S from './WBReactTable.styles';

// A wrapper around react-table that adds our paging and search.
import _ from 'lodash';
import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import classNames from 'classnames';
import ReactTable, {Column, SortingRule} from 'react-table';
import {Button, Input, Menu} from 'semantic-ui-react';

import {fuzzyMatchWithMapping} from '../util/fuzzyMatch';
import LegacyWBIcon from './elements/LegacyWBIcon';
import makeComp from '../util/profiler';
import {usePrevious} from '../state/hooks';
import {WBMenuOnSelectHandler} from './elements/WBMenu';

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const DEFAULT_PAGE_SIZE = 5;

export interface DataRow {
  searchString: string;
  row: any;
}

interface WBReactTableProps {
  /**
   * keys are the table header
   * data is a dictionary that has string values of
   * each key for each row
   * searchResultColumns is function that returns a different set of column
   *     renderers to use when we need to highlight search results
   */
  className?: string;
  data: DataRow[];
  loading?: boolean;
  columns: Column[];
  searchResultColumns?: (searchQuery: string) => Column[];
  noPaging?: boolean;
  noSearch?: boolean;
  noHeader?: boolean;
  topHeader?: [number, number, string];
  pageSize?: number;
  onChangePageSize?: (pageSize: number) => void;
  hasNextPage?: boolean;
  onFetchNextPage?: () => void;
  fetchingNextPage?: boolean;
  pageSizeOptions?: number[];
  getTrProps?: any;
  sortable?: boolean;
  defaultSorted?: SortingRule[];
  extraActions?: JSX.Element;
  style?: React.CSSProperties;
  resizable?: boolean;
  dataTest?: string;
  searchPlaceholder?: string;
}

const WBReactTable: React.FC<WBReactTableProps> = makeComp(
  ({
    className,
    data,
    loading,
    columns,
    searchResultColumns,
    noPaging,
    noSearch,
    noHeader,
    pageSize: propsPageSize,
    onChangePageSize,
    hasNextPage = false,
    onFetchNextPage,
    fetchingNextPage = false,
    pageSizeOptions: propsPageSizeOptions,
    getTrProps,
    sortable,
    defaultSorted,
    extraActions,
    style,
    resizable,
    dataTest,
    searchPlaceholder,
  }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);

    const [statePageSize, setPageSize] = useState<number | undefined>();
    const changePageSize = useCallback(
      (newPageSize: number) => {
        setPageSize(newPageSize);
        onChangePageSize?.(newPageSize);
      },
      [onChangePageSize]
    );

    const searchInputRef = useRef<Input | null>(null);
    const pageSizeOptions = propsPageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;

    if (!_.isEmpty(searchQuery)) {
      data = fuzzyMatchWithMapping(data, searchQuery, d => d.searchString);
      if (searchResultColumns) {
        columns = searchResultColumns(searchQuery);
      }
    }

    let pageSize = 1;
    if (statePageSize == null) {
      // pageSize is unset by the user so far
      if (data.length > 0) {
        pageSize = propsPageSize ?? DEFAULT_PAGE_SIZE;
      }
    } else {
      pageSize = statePageSize;
    }
    if (noPaging) {
      pageSize = data.length;
    }
    pageSize = Math.min(pageSize, data.length);

    // if the dataset or page size changes, we must update
    // page to ensure that it remains within bounds
    useLayoutEffect(() => {
      if (data.length === 0) {
        return;
      }
      const lastPage = Math.floor((data.length - 1) / pageSize);
      if (page > lastPage) {
        setPage(lastPage);
      }
    }, [page, pageSize, data.length]);

    // go to the freshly fetched page after fetching
    const prevFetchingNextPage = usePrevious(fetchingNextPage);
    useEffect(() => {
      if (prevFetchingNextPage && !fetchingNextPage) {
        setPage(prev => prev + 1);
      }
    }, [prevFetchingNextPage, fetchingNextPage]);

    return (
      <div
        className={classNames('wb-react-table', className)}
        data-test={dataTest}
        style={style ?? {}}>
        <ReactTable
          // className={striped ? '-striped' : ''}
          loading={loading}
          data={_.map(data, x => x.row)}
          columns={columns}
          page={page}
          pageSize={pageSize}
          resizable={resizable ?? false}
          pageSizeOptions={pageSizeOptions}
          showPaginationTop={false}
          showPaginationBottom={false}
          showPageJump={false}
          sortable={sortable}
          defaultSorted={defaultSorted}
          getTrProps={getTrProps}>
          {(state, makeTable, instance) => {
            return (
              <>
                {!noHeader && (
                  <Menu secondary style={{margin: '0px', overflowX: 'auto'}}>
                    {!noSearch && (
                      <Menu.Item>
                        <Input
                          className="searchbox"
                          icon={{
                            className: 'wbic-ic-search',
                          }}
                          iconPosition="left"
                          ref={searchInputRef}
                          placeholder={searchPlaceholder ?? 'Search'}
                          onChange={_.debounce((e, {value}) => {
                            setSearchQuery(value);
                          }, 200)}
                        />
                      </Menu.Item>
                    )}
                    {!noPaging && (
                      <Menu.Menu position="right">
                        {extraActions && (
                          <Menu.Item>
                            <Button.Group>{extraActions}</Button.Group>
                          </Menu.Item>
                        )}
                        <Menu.Item>
                          <S.RowCountSelectWrapper>
                            <span className="select-label">Rows per page</span>
                            <div>
                              <S.RowCountSelect
                                options={state.pageSizeOptions.map(size => {
                                  return {name: `${size}`, value: size};
                                })}
                                value={pageSize}
                                autoMenuWidth
                                onSelect={
                                  changePageSize as WBMenuOnSelectHandler
                                }>
                                Rows per page
                              </S.RowCountSelect>
                            </div>
                          </S.RowCountSelectWrapper>
                        </Menu.Item>
                        <Menu.Item>
                          <S.RowContextWrapper>
                            {((state.page || 0) + 1) * pageSize -
                              (pageSize - 1)}
                            {'-'}
                            {(state.page || 0) + 1 >
                              Math.floor(data.length / pageSize) &&
                            data.length % pageSize !== 0
                              ? data.length
                              : ((state.page || 0) + 1) * pageSize}
                            {' of '}
                            {data.length}
                            {hasNextPage && '+'}
                          </S.RowContextWrapper>
                        </Menu.Item>
                        <Menu.Item>
                          <S.PageNav>
                            <Button.Group>
                              <Button
                                size="tiny"
                                className="wb-icon-button only-icon"
                                disabled={
                                  !state.canPrevious || fetchingNextPage
                                }
                                onClick={() => setPage(prev => prev - 1)}>
                                <LegacyWBIcon name="previous" />
                              </Button>
                              <Button
                                size="tiny"
                                className="wb-icon-button only-icon"
                                disabled={
                                  !(
                                    state.canNext ||
                                    (onFetchNextPage != null && hasNextPage)
                                  ) || fetchingNextPage
                                }
                                loading={fetchingNextPage}
                                onClick={() => {
                                  if (state.canNext) {
                                    setPage(prev => prev + 1);
                                  } else if (
                                    onFetchNextPage != null &&
                                    hasNextPage
                                  ) {
                                    onFetchNextPage();
                                  }
                                }}>
                                <LegacyWBIcon
                                  name="next"
                                  style={{
                                    visibility: fetchingNextPage
                                      ? 'hidden'
                                      : 'visible',
                                  }}
                                />
                              </Button>
                            </Button.Group>
                          </S.PageNav>
                        </Menu.Item>
                      </Menu.Menu>
                    )}
                  </Menu>
                )}
                {makeTable()}
              </>
            );
          }}
        </ReactTable>
      </div>
    );
  },
  {id: 'WBReactTable', memo: true}
);

export default WBReactTable;
